import React, { useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';
import { navigateToSettings } from '@repo/redux/actions/ui';
import { useTheme } from '@repo/themes';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Avatar } from '@repo/components/avatars';
import { ProBadge, TeamsBadge } from '@repo/icons/ProductNameBadge';
import { LogoutIcon } from '@repo/icons';
import { panelTitle } from '../panels';
import iconForSection from './SettingsNavIcons';
import styles from './SettingsNav.module.scss';
import { useIsProd, useAuthContext } from '@repo/hooks';
import { useWorkspaceContext } from '@repo/hooks';
import { getPlanType } from '../panels/Profile/ManagePlan';
import { SettingsPanel } from '@repo/utils/types';

function NavHeader() {
  const authCtx = useAuthContext();
  const user = useRef(authCtx.user);
  const planType = useRef(getPlanType(authCtx));

  useEffect(() => {
    user.current = authCtx.user;
    planType.current = getPlanType(authCtx);
  }, [authCtx.user]);

  return (
    <div className={cn(styles.navItem, styles.myAccountNavItem)}>
      <div className={styles.avatarBlur}>
        <img src={user.current?.avatar_url} />
      </div>
      <Avatar url={user.current?.avatar_url} className={styles.avatar} />
      <div className={styles.nameContainer}>
        <span className={styles.name}>
          {user.current?.first_name} {user.current?.last_name}
        </span>
        {planType.current === 'pro' && <ProBadge />}
        {planType.current === 'teams' && <TeamsBadge />}
      </div>
      <div className={styles.email}>{user.current?.email}</div>
    </div>
  );
}

function PanelNavDisplay({ section }: { section: SettingsPanel }) {
  const [theme] = useTheme();
  const Icon = iconForSection[section];

  return (
    <>
      <div className={cn(styles.settingsIconBox, theme === 'light' && styles.light)}>
        <Icon />
      </div>
      <span>{panelTitle(section)}</span>
    </>
  );
}

const adminOnlyPanels = new Set();

const panels: SettingsPanel[] = [
  'general',
  'profile',
  'home',
  'integrations',
  'notifications',
  'appearance',
  'about',
  'staging',
];

const workspacePanels: SettingsPanel[] = ['workspace-general', 'workspace-members'];

const PanelNavLink = ({
  panel,
  renderedPanel,
  renderAsRoute,
}: {
  panel: SettingsPanel;
  renderedPanel: SettingsPanel;
  renderAsRoute: boolean;
}) => {
  const dispatch = useDispatch();
  const isProd = useIsProd();
  const { userIsAdmin } = useWorkspaceContext();
  const route = `/settings/${panel}`;
  const className = cn(styles.navItem, panel === renderedPanel && styles.active);

  if (isProd && panel === 'staging') {
    return null;
  }

  if (!userIsAdmin && adminOnlyPanels.has(panel)) {
    return null;
  }

  if (renderAsRoute) {
    return (
      <Link className={className} key={panel} to={route}>
        <PanelNavDisplay section={panel} />
      </Link>
    );
  }

  return (
    <button
      className={className}
      key={panel}
      onClick={() => dispatch(navigateToSettings({ panel }))}
    >
      <PanelNavDisplay section={panel} />
    </button>
  );
};

export default function SettingsNav({
  renderAsRoute,
  renderedPanel,
}: {
  renderAsRoute: boolean;
  renderedPanel: SettingsPanel;
}) {
  const [theme] = useTheme();
  const { cleanupAndLogout } = useAuthContext();
  const { activeWorkspace } = useWorkspaceContext();

  const renderPanelLink = useCallback(
    (panel: SettingsPanel, idx: number) => (
      <PanelNavLink
        key={idx}
        panel={panel}
        renderedPanel={renderedPanel}
        renderAsRoute={renderAsRoute}
      />
    ),
    [renderedPanel],
  );

  return (
    <div className={cn(styles.nav, styles[theme])}>
      <NavHeader />
      {panels.map(renderPanelLink)}
      {activeWorkspace && (
        <>
          <div className={cn(styles.sectionTitle, 'information')}>Workspace</div>
          {workspacePanels.map(renderPanelLink)}
        </>
      )}
      <button className={styles.logout} onClick={cleanupAndLogout}>
        <LogoutIcon width={14} /> Sign Out
      </button>
    </div>
  );
}
